import React, { useEffect } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';

import { withFlags } from '@confluence/flags';
import {
	confluenceLocalStorageInstance as localStorage,
	keys as LOCALSTORAGE_KEYS,
} from '@confluence/storage-manager';
import { useFeatureDiscovery } from '@confluence/feature-discovery';

import { useMigratedRecently } from '../hooks/useMigratedRecently';
import {
	PAGE_HAS_MIGRATED_FEATURE_KEY,
	MIGRATION_FLAG_AUTO_CLOSE_TIMEOUT,
	learnMoreURL,
} from '../constants';

const i18n = defineMessages({
	pageHasBeenConvertedTitle: {
		id: 'editor-features.page-has-been-converted-title',
		description:
			'Page Opt In Migration Flag title to show that page has been recently converted to Fabric',
		defaultMessage: 'Page has been converted',
	},
	pageHasBeenConvertedDescription: {
		id: 'editor-features.page-has-been-converted-description',
		description:
			'Page Opt In Migration Flag description to show that page has been recently converted to Fabric',
		defaultMessage: 'This page has been converted to the new editing experience',
	},
	pageHasBeenConvertedLearnMore: {
		id: 'editor-features.page-has-been-converted-learn-more-link',
		description:
			'Page Opt In Migration Flag call to action to direct user to url where they learn more about pages converted to new fabric editor',
		defaultMessage: 'Learn more',
	},
});

const onClickLearnMore = () => {
	window.open(learnMoreURL, '_blank');
};

const MigrationSuccessFlagComponent = ({ contentId, flags }) => {
	const migratedRecently = useMigratedRecently(contentId);
	const hasLocalStorageValue = localStorage.getItem(LOCALSTORAGE_KEYS.PAGE_JUST_CONVERTED);

	const [shouldShowMigrationFlag, handleStop] = useFeatureDiscovery(
		`${PAGE_HAS_MIGRATED_FEATURE_KEY}.${contentId}`,
	);

	const shouldShow =
		shouldShowMigrationFlag && !!hasLocalStorageValue && migratedRecently && !!flags;

	useEffect(() => {
		if (shouldShow) {
			void flags
				.showInfoFlag({
					title: <FormattedMessage {...i18n.pageHasBeenConvertedTitle} />,
					description: <FormattedMessage {...i18n.pageHasBeenConvertedDescription} />,
					actions: [
						{
							content: <FormattedMessage {...i18n.pageHasBeenConvertedLearnMore} />,
							onClick: onClickLearnMore,
						},
					],
				})
				.then((flag) => {
					window.setTimeout(
						(flagID) => {
							void flags.hideFlag(flagID);
						},
						MIGRATION_FLAG_AUTO_CLOSE_TIMEOUT,
						flag.id,
					);

					handleStop();
				});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [shouldShow]);

	return null;
};

export const MigrationSuccessFlag = withFlags(MigrationSuccessFlagComponent);
